import React, { useState } from 'react';
import PropTypes from 'prop-types';
import copy from 'clipboard-copy';

import { Row, Col } from 'react-bootstrap';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLang } from 'app/context/i18n';

import { Colors } from 'app/utils/colors';
import Button from 'app/components/Button';
import Icon from 'app/components/Icon';

import 'app/modules/MyAccount/pages/Syncronize/components/SynchronizeWithBigBuyApiKey.scss';

function ApiKeyField({
  label,
  value,
  onToggleVisibility,
  onCopy,
  onGenerate,
  showGenerate = false,
  isVisible = false
}) {
  const intl = useIntl();
  return (
    <Row className="bb-synchronize-api-key__row-info">
      <Col md={3}>
        <p className="bb-synchronize-api-key__p-info">{label}</p>
      </Col>
      <Col md={7}>
        <div className="bb-synchronize-api-key__input-wrapper">
          <div className="bb-synchronize-api-key__input-group">
            <input
              type={isVisible ? 'text' : 'password'}
              className="bb-synchronize-api-key__input"
              value={isVisible ? value : '••••••'}
              placeholder="••••••"
              disabled
              readOnly
            />
            <div className="bb-synchronize-api-key__actions">
              <Button
                type="default"
                size="small"
                onClick={onToggleVisibility}
                icon={
                  <Icon
                    icon={isVisible ? 'eye-slash' : 'eye'}
                    color={Colors.bbSecondary}
                    size="sm"
                  />
                }
                className="bb-synchronize-api-key__action-btn"
                tooltipText={intl.formatMessage({
                  id: 'controlpanel.my_account.synchronize.api_key.tooltip.view'
                })}
              />
              <Button
                type="default"
                size="small"
                onClick={onCopy}
                icon={<Icon icon="copy" color={Colors.bbSecondary} size="sm" />}
                className="bb-synchronize-api-key__action-btn"
                tooltipText={intl.formatMessage({
                  id: 'controlpanel.my_account.synchronize.api_key.tooltip.copy'
                })}
              />
              {showGenerate && (
                <Button
                  type="secondary"
                  size="small"
                  onClick={onGenerate}
                  icon={<Icon icon="sync" color={Colors.bbWhite} size="sm" />}
                  className="bb-synchronize-api-key__action-btn"
                  tooltipText={intl.formatMessage({
                    id: 'controlpanel.my_account.synchronize.api_key.tooltip.change'
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}

ApiKeyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onGenerate: PropTypes.func,
  showGenerate: PropTypes.bool,
  isVisible: PropTypes.bool
};

function SynchronizeInactive({ onShowApiKey, disabled }) {
  const intl = useIntl();

  return (
    <Row>
      <Col md={{ offset: 3, span: 7 }}>
        <Button
          text={intl.formatMessage({
            id: 'controlpanel.my_account.synchronize.api_key.button'
          })}
          type="primary"
          size="big"
          typeButton="button"
          disabled={disabled}
          onClick={onShowApiKey}
          icon={<Icon icon="eye" color={Colors.bbGrey2} size="lg" />}
        />
      </Col>
    </Row>
  );
}

SynchronizeInactive.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onShowApiKey: PropTypes.func.isRequired
};

function SynchronizeActive({
  apiKeySandbox,
  apiKeyProduction,
  onToggleSandboxVisibility,
  onToggleProductionVisibility,
  onCopySandbox,
  onCopyProduction,
  onGenerateSandbox,
  onGenerateProduction,
  sandboxVisible,
  productionVisible
}) {
  const intl = useIntl();

  return (
    <>
      <ApiKeyField
        label={intl.formatMessage({
          id: 'controlpanel.my_account.synchronize.api_key.production'
        })}
        value={apiKeyProduction}
        onToggleVisibility={onToggleProductionVisibility}
        onCopy={onCopyProduction}
        onGenerate={onGenerateProduction}
        showGenerate
        isVisible={productionVisible}
      />
      <ApiKeyField
        label={intl.formatMessage({
          id: 'controlpanel.my_account.synchronize.api_key.sandbox'
        })}
        value={apiKeySandbox}
        onToggleVisibility={onToggleSandboxVisibility}
        onCopy={onCopySandbox}
        onGenerate={onGenerateSandbox}
        showGenerate={false}
        isVisible={sandboxVisible}
      />
    </>
  );
}

SynchronizeActive.propTypes = {
  apiKeySandbox: PropTypes.string.isRequired,
  apiKeyProduction: PropTypes.string.isRequired,
  onToggleSandboxVisibility: PropTypes.func.isRequired,
  onToggleProductionVisibility: PropTypes.func.isRequired,
  onCopySandbox: PropTypes.func.isRequired,
  onCopyProduction: PropTypes.func.isRequired,
  onGenerateSandbox: PropTypes.func.isRequired,
  onGenerateProduction: PropTypes.func.isRequired,
  sandboxVisible: PropTypes.bool.isRequired,
  productionVisible: PropTypes.bool.isRequired
};

export default function SynchronizeWithBigBuyApiKey({
  background = Colors.bbWhite,
  apiKeySandbox = '',
  apiKeyProduction = '',
  onToggleSandboxVisibility,
  onToggleProductionVisibility,
  onCopySandbox,
  onCopyProduction,
  onGenerateProduction
}) {
  const intl = useIntl();
  const lang = useLang();
  const [sandboxVisible, setSandboxVisible] = useState(false);
  const [productionVisible, setProductionVisible] = useState(false);

  const handleToggleSandboxVisibility = () => {
    setSandboxVisible(!sandboxVisible);
    if (onToggleSandboxVisibility) {
      onToggleSandboxVisibility();
    }
  };

  const handleToggleProductionVisibility = () => {
    setProductionVisible(!productionVisible);
    if (onToggleProductionVisibility) {
      onToggleProductionVisibility();
    }
  };

  const copyToClipboard = async (text, onCopyCallback) => {
    try {
      await copy(text);
      if (onCopyCallback) {
        onCopyCallback();
      }
    } catch (error) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        if (onCopyCallback) {
          onCopyCallback();
        }
      } catch (fallbackError) {
        // Si ambos métodos fallan, no hacer nada
        // En un entorno de producción, aquí se podría mostrar un toast de error
      }
    }
  };

  const handleCopySandbox = () => {
    copyToClipboard(apiKeySandbox, onCopySandbox);
  };

  const handleCopyProduction = () => {
    copyToClipboard(apiKeyProduction, onCopyProduction);
  };

  const handleGenerateProduction = () => {
    if (onGenerateProduction) {
      onGenerateProduction();
    }
  };

  return (
    <div className="bb-synchronize-api-key" style={{ background }}>
      <Row>
        <Col md={{ offset: 3, span: 7 }}>
          <p className="big medium">
            {intl.formatMessage({
              id: 'controlpanel.my_account.synchronize.api_key.title'
            })}
          </p>
          <p className="bb-synchronize-api-key__p-des">
            <FormattedMessage
              id="controlpanel.my_account.synchronize.api_key.text"
              values={{
                a: (...chunks) => (
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={
                      lang === 'es'
                        ? 'https://www.bigbuy.eu/public/doc/Guia_API_BigBuy_ES.pdf'
                        : 'https://www.bigbuy.eu/public/doc/Guia_API_BigBuy_EN.pdf'
                    }
                  >
                    {chunks}
                  </a>
                )
              }}
            />
          </p>
          <p className="bb-synchronize-api-key__p-des">
            <FormattedMessage
              id="controlpanel.my_account.synchronize.api_key.text2"
              values={{
                a: (...chunks) => (
                  <a rel="noopener noreferrer" target="_blank" href={chunks}>
                    {chunks}
                  </a>
                )
              }}
            />
          </p>
        </Col>
      </Row>
      <SynchronizeActive
        apiKeySandbox={apiKeySandbox}
        apiKeyProduction={apiKeyProduction}
        onToggleSandboxVisibility={handleToggleSandboxVisibility}
        onToggleProductionVisibility={handleToggleProductionVisibility}
        onCopySandbox={handleCopySandbox}
        onCopyProduction={handleCopyProduction}
        onGenerateProduction={handleGenerateProduction}
        sandboxVisible={sandboxVisible}
        productionVisible={productionVisible}
      />
    </div>
  );
}

SynchronizeWithBigBuyApiKey.propTypes = {
  background: PropTypes.string,
  apiKeySandbox: PropTypes.string,
  apiKeyProduction: PropTypes.string,
  onToggleSandboxVisibility: PropTypes.func,
  onToggleProductionVisibility: PropTypes.func,
  onCopySandbox: PropTypes.func,
  onCopyProduction: PropTypes.func,
  onGenerateProduction: PropTypes.func
};
